import { useEffect, useMemo, useState } from "react";
import type { OpsEvent, OpsPayload, OpsStat } from "../types";

const KINDS: { key: string; label: string }[] = [
  { key: "all", label: "All" },
  { key: "activity", label: "Activity" },
  { key: "meeting", label: "Meetings" },
  { key: "decision", label: "Decisions" },
  { key: "gap", label: "Gaps" },
  { key: "dev", label: "Dev" },
];

/** Colour per kind so a long timeline stays scannable. */
const KIND_CLASS: Record<string, string> = {
  activity: "k-activity",
  meeting: "k-meeting",
  decision: "k-decision",
  gap: "k-gap",
  dev: "k-dev",
};

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const mins = (Date.now() - d.getTime()) / 60000;
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.floor(mins)}m ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface Props {
  onBack: () => void;
}

export function OpsCenter({ onBack }: Props) {
  const [kind, setKind] = useState("all");
  const [data, setData] = useState<OpsPayload | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/ops?kind=${encodeURIComponent(kind)}&limit=200`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((d: OpsPayload) => {
        if (!cancelled) {
          setData(d);
          setError("");
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const events: OpsEvent[] = data?.events ?? [];
  const stats: OpsStat[] = data?.stats ?? [];

  // Group by day so a scroll reads as a diary rather than an undifferentiated feed.
  const days = useMemo(() => {
    const out: { day: string; items: OpsEvent[] }[] = [];
    for (const e of events) {
      const day = (e.at ?? "").slice(0, 10);
      const last = out[out.length - 1];
      if (last && last.day === day) last.items.push(e);
      else out.push({ day, items: [e] });
    }
    return out;
  }, [events]);

  return (
    <div className="app">
      <header className="header">
        <button className="back" onClick={onBack} aria-label="Back to round">
          ← Round
        </button>
        <h1>Ops centre</h1>
        <div className="sub">Everything the council has done</div>
      </header>

      <main className="main detail">
        {stats.length > 0 && (
          <section className="stats">
            {stats.map((s) => (
              <div key={s.key} className="stat">
                <span className="stat-v">{s.value}</span>
                <span className="stat-l">{s.label}</span>
                {s.note && <span className="stat-n">{s.note}</span>}
              </div>
            ))}
          </section>
        )}

        <div className="chips filters">
          {KINDS.map((k) => (
            <button
              key={k.key}
              className="chip filter"
              aria-pressed={kind === k.key}
              onClick={() => setKind(k.key)}
            >
              {k.label}
              {data?.byKind[k.key] != null && (
                <span className="chip-n"> {data.byKind[k.key]}</span>
              )}
            </button>
          ))}
        </div>

        {loading && (
          <div className="state">
            <div className="spinner" />
          </div>
        )}

        {error && (
          <section className="panel unseen">
            <h2>Could not load</h2>
            <p>{error}</p>
          </section>
        )}

        {!loading && !error && events.length === 0 && (
          <section className="panel">
            <h2>Nothing logged yet</h2>
            <p className="muted">
              The council writes here as it works — standups, claims, verdicts, refusals
              and capability gaps. Publish the local logs with{" "}
              <code>npm run publish-ops</code>.
            </p>
          </section>
        )}

        {days.map(({ day, items }) => (
          <section key={day} className="day">
            <h2 className="day-h">
              {new Date(day).toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}
            </h2>
            <ul className="feed">
              {items.map((e) => (
                <li key={e.id} className={`ev ${KIND_CLASS[e.kind] ?? ""}`}>
                  <div className="ev-head">
                    <span className="ev-kind">{e.kind}</span>
                    {e.actor && <span className="ev-actor">{e.actor}</span>}
                    <span className="ev-when">{when(e.at)}</span>
                  </div>
                  <div className="ev-title">{e.title}</div>
                  {e.detail && <pre className="ev-detail">{e.detail}</pre>}
                  {e.ref && <div className="src">{e.ref}</div>}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </main>
    </div>
  );
}
